import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Mail, Loader2, CheckCircle2, AlertCircle } from 'lucide-react'
import { authService } from '@/services/auth'

export default function ForgotPasswordPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      if (!email || !email.includes('@')) {
        throw new Error(t('auth.errors.invalidEmail'))
      }
      await authService.requestPasswordReset(email)
      setSuccess(t('auth.forgotPasswordSuccess'))
    } catch (err) {
      // Güvenlik nedeni ile genel mesaj göster
      setSuccess(t('auth.forgotPasswordSuccess'))
      if (err instanceof Error) {
        console.debug('Forgot password error:', err.message)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-2">
            {t('auth.forgotPasswordTitle')}
          </h1>
          <p className="text-white/60 text-lg">
            {t('auth.forgotPasswordSubtitle')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-6 md:p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="flex items-center space-x-3 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-red-300">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            {success && (
              <div className="flex items-center space-x-3 p-4 bg-emerald-500/20 border border-emerald-500/30 rounded-xl text-emerald-300">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{success}</span>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-white/80 text-sm font-medium">{t('auth.email')}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field pl-11"
                  placeholder={t('auth.emailPlaceholder') as string}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t('auth.forgotPasswordSending')}</span>
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  <span>{t('auth.forgotPasswordSend')}</span>
                </>
              )}
            </button>
          </form>

          <div className="text-center mt-6">
            <p className="text-white/60 text-sm">
              {t('auth.forgotPasswordBackToLogin')} {' '}
              <Link to="/login" className="text-gradient hover:text-gradient-gold transition-colors font-medium">
                {t('auth.signIn')}
              </Link>
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-8"
        >
          <button
            onClick={() => navigate(-1)}
            className="text-white/40 hover:text-white/60 transition-colors text-sm"
          >
            ← {t('common.back')}
          </button>
        </motion.div>
      </div>
    </div>
  )
}



